import type {
  SecurityConfig,
  SecurityValidationResult,
  SecurityViolation
} from '../types/security';
import type { Action } from '../types/actions';
import { EventBus } from '../core/event-bus';
import { Logger } from '../core/logger';
import {
  validateNavigationTarget,
  validateCurrentDomain
} from '../utils/domain-validator';
import {
  sanitizeInput,
  sanitizeSelector,
  sanitizeUrl
} from '../utils/input-sanitizer';

export class SecurityManager {
  private config: SecurityConfig;
  private eventBus: EventBus;
  private logger: Logger;
  private violations: SecurityViolation[];

  constructor(
    eventBus: EventBus,
    logger: Logger,
    allowedDomains: string[] = []
  ) {
    this.eventBus = eventBus;
    this.logger = logger;
    this.violations = [];

    this.config = {
      allowedDomains,
      blockDangerousSelectors: true,
      sanitizeInputs: true,
      maxSelectorLength: 1000,
      maxInputLength: 10000
    };

    this.logger.debug('SecurityManager initialized', { config: this.config });
  }

  /**
   * Validate an action before execution
   */
  validateAction(action: Action): SecurityValidationResult {
    const actionType = action.type;

    switch (actionType) {
      case 'navigate':
        return this.validateNavigateAction(action);

      case 'click':
      case 'fill':
      case 'submit':
      case 'scroll':
      case 'select':
      case 'hover':
      case 'focus':
      case 'blur':
        return this.validateSelectorAction(action);

      default:
        return { allowed: true };
    }
  }

  /**
   * Validate navigate action
   */
  private validateNavigateAction(action: any): SecurityValidationResult {
    const url = action.url;

    if (!url) {
      return {
        allowed: false,
        reason: 'URL is required for navigate action'
      };
    }

    // Sanitize URL
    const sanitized = sanitizeUrl(url);
    if (sanitized.removedPatterns.length > 0) {
      this.recordViolation({
        type: 'url',
        severity: 'high',
        message: `Dangerous patterns removed from URL: ${sanitized.removedPatterns.join(', ')}`,
        blocked: !sanitized.sanitized,
        timestamp: Date.now()
      });
    }

    if (!sanitized.sanitized) {
      return {
        allowed: false,
        reason: 'URL failed sanitization'
      };
    }

    // Validate domain
    const domainValidation = validateNavigationTarget(
      sanitized.sanitized,
      this.config.allowedDomains
    );

    if (!domainValidation.allowed) {
      this.recordViolation({
        type: 'domain',
        severity: 'high',
        message: domainValidation.reason || 'Domain not allowed',
        blocked: true,
        timestamp: Date.now()
      });

      return {
        allowed: false,
        reason: domainValidation.reason
      };
    }

    return {
      allowed: true,
      sanitized: { ...action, url: sanitized.sanitized }
    };
  }

  /**
   * Validate action with selector
   */
  private validateSelectorAction(action: any): SecurityValidationResult {
    const selector = action.selector;

    if (!selector) {
      return { allowed: true };
    }

    // Sanitize selector
    if (this.config.blockDangerousSelectors) {
      const sanitized = sanitizeSelector(selector, this.config.maxSelectorLength);

      if (sanitized.removedPatterns.length > 0) {
        this.recordViolation({
          type: 'selector',
          severity: 'high',
          message: `Dangerous patterns in selector: ${sanitized.removedPatterns.join(', ')}`,
          blocked: !sanitized.sanitized,
          timestamp: Date.now()
        });
      }

      if (!sanitized.sanitized) {
        return {
          allowed: false,
          reason: 'Selector failed security validation'
        };
      }

      action = { ...action, selector: sanitized.sanitized };
    }

    // Sanitize value/input if present
    if (this.config.sanitizeInputs && action.value !== undefined) {
      const sanitized = sanitizeInput(
        String(action.value),
        this.config.maxInputLength
      );

      if (sanitized.removedPatterns.length > 0) {
        this.recordViolation({
          type: 'input',
          severity: 'medium',
          message: `Input sanitized: ${sanitized.removedPatterns.join(', ')}`,
          blocked: false,
          timestamp: Date.now()
        });
      }

      action = { ...action, value: sanitized.sanitized };
    }

    return {
      allowed: true,
      sanitized: action
    };
  }

  /**
   * Check if current domain is allowed
   */
  checkCurrentDomain(): SecurityValidationResult {
    if (this.config.allowedDomains.length === 0) {
      return { allowed: true };
    }

    const validation = validateCurrentDomain(this.config.allowedDomains);

    if (!validation.allowed) {
      this.recordViolation({
        type: 'domain',
        severity: 'high',
        message: validation.reason || 'Current domain not allowed',
        blocked: true,
        timestamp: Date.now()
      });
    }

    return {
      allowed: validation.allowed,
      reason: validation.reason
    };
  }

  /**
   * Record security violation
   */
  private recordViolation(violation: SecurityViolation): void {
    this.violations.push(violation);
    this.eventBus.emit('security:violation', violation);
    this.logger.warn('Security violation', violation);

    // Keep only last 100 violations
    if (this.violations.length > 100) {
      this.violations = this.violations.slice(-100);
    }
  }

  /**
   * Get all recorded violations
   */
  getViolations(): SecurityViolation[] {
    return [...this.violations];
  }

  /**
   * Clear violation history
   */
  clearViolations(): void {
    this.violations = [];
    this.logger.debug('Security violations cleared');
  }

  /**
   * Update security configuration
   */
  updateConfig(config: Partial<SecurityConfig>): void {
    this.config = { ...this.config, ...config };
    this.logger.debug('Security config updated', { config: this.config });
  }

  /**
   * Get current security configuration
   */
  getConfig(): SecurityConfig {
    return { ...this.config };
  }
}
